import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import html2pdf from 'html2pdf.js';
import FAD07Laboratorio from './FAD07Laboratorio';
import AlertaLaboratorioSemana, { GrupoConferencia } from './AlertaLaboratorioSemana';
import BotaoArquivar from './BotaoArquivar';
import { AVISO_D1, avisosDaConferencia } from './arquivarCopia';
import {
  Box, Typography, CircularProgress, Alert, Divider,
  FormControl, InputLabel, Select, MenuItem,
  Button, SelectChangeEvent, Grid, TextField,
  Accordion, AccordionSummary, AccordionDetails,
  Table, TableHead, TableBody, TableRow, TableCell,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ModuleHeader from './components/ModuleHeader';
import SummarizeIcon from '@mui/icons-material/Summarize';

const API = '/api';

// ── Types ──────────────────────────────────────────────────────────────────
interface Area  { id: number; nome: string; zona: string }
interface Ciclo { id: number; nome: string }
interface SeInfo { se: number; data_inicio: string; data_fim: string }

interface Computado {
  residencias: number; comercios: number; terrenos_baldios: number;
  pontos_estrategicos: number; outros: number; total_imoveis: number;
  trat_focal: number; trat_perifocal: number; inspecionados: number;
  recusados: number; fechados: number; recuperados: number;
  imoveis_positivos: number; dep_positivos: number; amostras_coletadas: number;
  quarteiroes_concluidos_count: number; quarteiroes_trabalhados_count: number;
  agentes_trabalhando: number; dias_trabalhados: number;
  dep_a1: number; dep_a2: number; dep_b: number; dep_c: number;
  dep_d1: number; dep_d2: number; dep_e: number; dep_total: number;
  dep_eliminados: number; dep_tratados: number;
  larvicida_l1_tipo: string | null; larvicida_l1_gramas: number;
  larvicida_l1_depositos: number; multiplos_l1: boolean;
  larvicida_l2_tipo: string | null; larvicida_l2_gramas: number;
  larvicida_l2_depositos: number; multiplos_l2: boolean;
  adulticida_tipo: string | null; adulticida_qtd_ml: number;
  adulticida_cargas: number; multiplos_adulticida: boolean;
  atividade_mais_freq: string | null; multiplas_atividades: boolean;
  ibi: number; iip: number; ib: number;
  lab_dep_aeg: Record<string, number>; lab_dep_alb: Record<string, number>;
  lab_imov_aeg: Record<string, number>; lab_imov_alb: Record<string, number>;
  lab_ex_aeg: Record<string, number>;  lab_ex_alb: Record<string, number>;
  lab_qrt_aegypti: string[]; lab_qrt_albopictus: string[]; lab_qrt_intersecao: string[];
}

interface Manual {
  id: number | null;
  atividade: string | null;
  imoveis_programados: number;
  agentes_programados: number;
  quarteiroes_programados: number;
  visto_supervisor: string | null;
  visto_data: string | null;
  pendencias: string | null;
}

interface DadosFAD07 {
  se: number;
  data_inicio: string;
  data_fim: string;
  total_registros: number;
  area: Area | null;
  ciclo: Ciclo | null;
  computado: Computado;
  manual: Manual;
}

interface DadosManuais {
  tipo: string;
  data_inicio: string;
  data_final: string;
  concluido: string;
  atividade: string;
  localidade: string;
  zona: string;
  ciclo_ano: string;
  sem_epidem: string;
  lab_dep_aeg_a1: number; lab_dep_aeg_a2: number; lab_dep_aeg_b: number; lab_dep_aeg_c: number;
  lab_dep_aeg_d1: number; lab_dep_aeg_d2: number; lab_dep_aeg_e: number; lab_dep_aeg_total: number;
  lab_dep_alb_a1: number; lab_dep_alb_a2: number; lab_dep_alb_b: number; lab_dep_alb_c: number;
  lab_dep_alb_d1: number; lab_dep_alb_d2: number; lab_dep_alb_e: number; lab_dep_alb_total: number;
  lab_imov_aeg_r: number; lab_imov_aeg_c: number; lab_imov_aeg_tb: number; lab_imov_aeg_pe: number;
  lab_imov_aeg_o: number; lab_imov_aeg_total: number; lab_imov_aeg_larvas: number; lab_imov_aeg_pupas: number;
  lab_imov_aeg_exuvia: number; lab_imov_aeg_adultos: number;
  lab_imov_alb_r: number; lab_imov_alb_c: number; lab_imov_alb_tb: number; lab_imov_alb_pe: number;
  lab_imov_alb_o: number; lab_imov_alb_total: number; lab_imov_alb_larvas: number; lab_imov_alb_pupas: number;
  lab_imov_alb_exuvia: number; lab_imov_alb_adultos: number;
  lab_imov_outros_r: number; lab_imov_outros_c: number; lab_imov_outros_tb: number; lab_imov_outros_pe: number;
  lab_imov_outros_o: number; lab_imov_outros_total: number; lab_imov_outros_larvas: number; lab_imov_outros_pupas: number;
  lab_imov_outros_exuvia: number; lab_imov_outros_adultos: number;
  lab_quart_aeg: string[];
  lab_quart_alb: string[];
  lab_quart_aeg_alb: string[];
  visto_supervisor: string;
  data_visto: string;
}

const DADOS_MANUAIS_DEFAULT: DadosManuais = {
  tipo: '',
  data_inicio: '',
  data_final: '',
  concluido: '',
  atividade: '2',
  localidade: '',
  zona: '',
  ciclo_ano: '',
  sem_epidem: '',
  lab_dep_aeg_a1: 0, lab_dep_aeg_a2: 0, lab_dep_aeg_b: 0, lab_dep_aeg_c: 0,
  lab_dep_aeg_d1: 0, lab_dep_aeg_d2: 0, lab_dep_aeg_e: 0, lab_dep_aeg_total: 0,
  lab_dep_alb_a1: 0, lab_dep_alb_a2: 0, lab_dep_alb_b: 0, lab_dep_alb_c: 0,
  lab_dep_alb_d1: 0, lab_dep_alb_d2: 0, lab_dep_alb_e: 0, lab_dep_alb_total: 0,
  lab_imov_aeg_r: 0, lab_imov_aeg_c: 0, lab_imov_aeg_tb: 0, lab_imov_aeg_pe: 0,
  lab_imov_aeg_o: 0, lab_imov_aeg_total: 0, lab_imov_aeg_larvas: 0, lab_imov_aeg_pupas: 0,
  lab_imov_aeg_exuvia: 0, lab_imov_aeg_adultos: 0,
  lab_imov_alb_r: 0, lab_imov_alb_c: 0, lab_imov_alb_tb: 0, lab_imov_alb_pe: 0,
  lab_imov_alb_o: 0, lab_imov_alb_total: 0, lab_imov_alb_larvas: 0, lab_imov_alb_pupas: 0,
  lab_imov_alb_exuvia: 0, lab_imov_alb_adultos: 0,
  lab_imov_outros_r: 0, lab_imov_outros_c: 0, lab_imov_outros_tb: 0, lab_imov_outros_pe: 0,
  lab_imov_outros_o: 0, lab_imov_outros_total: 0, lab_imov_outros_larvas: 0, lab_imov_outros_pupas: 0,
  lab_imov_outros_exuvia: 0, lab_imov_outros_adultos: 0,
  lab_quart_aeg:     Array(20).fill(''),
  lab_quart_alb:     Array(20).fill(''),
  lab_quart_aeg_alb: Array(20).fill(''),
  visto_supervisor:  '',
  data_visto:        new Date().toISOString().slice(0, 10),
};

// ── Helpers ────────────────────────────────────────────────────────────────
function semanaLabel(s: SeInfo): string {
  const ini = new Date(s.data_inicio + 'T12:00:00');
  const fim = new Date(s.data_fim    + 'T12:00:00');
  const fmt = (d: Date) => d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
  return `SE ${s.se} (${fmt(ini)} – ${fmt(fim)})`;
}

const spLabel = (num: number | string, text: string) => (
  <><sup style={{ color: '#9E9E9E', fontSize: '0.72em' }}>{num}</sup>{' '}{text}</>
);

const atividadeLabel = spLabel(12, 'Atividade');

const ATIVIDADES = [
  { value: '1', label: '1 - LI - Levantamento de índice' },
  { value: '2', label: '2 - LI+T - Levantamento de índice+Tratamento' },
  { value: '3', label: '3 - PE - Ponto estratégico' },
  { value: '4', label: '4 - T - Tratamento' },
  { value: '5', label: '5 - DF - Delimitação de Foco' },
  { value: '6', label: '6 - PVE - Pesquisa Vetorial Especial' },
];

const roTabNums = { readOnly: true as const, style: { fontVariantNumeric: 'tabular-nums' as const } };

// Filtros lembrados na aba (Bloco F2): ao voltar do FAD-01 o documento reabre na mesma semana.
const CHAVE_FILTROS = 'tovis-fad07-filtros';
type FiltrosFad07 = { areaId?: string; cicloId?: string; se?: string };
function lerFiltrosFad07(): FiltrosFad07 | null {
  try {
    const r = sessionStorage.getItem(CHAVE_FILTROS);
    const p: unknown = r ? JSON.parse(r) : null;
    return p && typeof p === 'object' ? (p as FiltrosFad07) : null;
  } catch {
    return null;
  }
}
function gravarFiltrosFad07(f: FiltrosFad07) {
  try {
    sessionStorage.setItem(CHAVE_FILTROS, JSON.stringify(f));
  } catch {
    // navegador sem armazenamento: segue sem lembrar os filtros
  }
}

// ── Component ──────────────────────────────────────────────────────────────
export default function FAD07({ onVoltar, onAbrirFAD01 }: { onVoltar?: () => void; onAbrirFAD01?: (id: number) => void }) {
  // Reference data
  const [areas,   setAreas]   = useState<Area[]>([]);
  const [ciclos,  setCiclos]  = useState<Ciclo[]>([]);
  const [semanas, setSemanas] = useState<SeInfo[]>([]);

  // Filter selections
  const [areaId,  setAreaId]  = useState<string>('');
  const [cicloId, setCicloId] = useState<string>('');
  const [se,      setSe]      = useState<string>('');

  // Data state
  const [dadosFAD07,   setDadosFAD07]   = useState<DadosFAD07 | null>(null);
  const [dadosManuais, setDadosManuais] = useState<DadosManuais>(DADOS_MANUAIS_DEFAULT);
  const [loading,      setLoading]      = useState(false);
  const [error,        setError]        = useState('');
  const [labExpandido, setLabExpandido] = useState(true);
  // Conferencia do laboratorio da semana (Sessao 29) — so leitura, nao muda numero
  const [conferencia, setConferencia] = useState<GrupoConferencia[] | null>(null);

  // Arquivos: dados do documento para o botão "Arquivar" (só existe quando o FAD-07 está na tela)
  const pedidoArquivo = useMemo(() => {
    if (!dadosFAD07 || !areaId || !cicloId || !se) return null;
    const nomeArea = areas.find(a => String(a.id) === String(areaId))?.nome ?? `Área ${areaId}`;
    const nomeCiclo = ciclos.find(c => String(c.id) === String(cicloId))?.nome ?? `Ciclo ${cicloId}`;
    return {
      pasta: 'fad07' as const,
      tipo: 'fad07',
      chave: `fad07|c${cicloId}|a${areaId}|se${se}`,
      titulo: `FAD-07 · ${nomeArea} · SE ${se} · ${nomeCiclo}`,
      ciclo_id: Number(cicloId),
      area_id: Number(areaId),
      se: Number(se),
      referencia: `SE ${se}`,
      avisos: [...avisosDaConferencia(conferencia), AVISO_D1],
    };
  }, [dadosFAD07, areaId, cicloId, se, areas, ciclos, conferencia]);

  const carregarConferencia = useCallback(() => {
    if (!areaId || !cicloId || !se) { setConferencia(null); return; }
    const qs = new URLSearchParams({ ciclo_id: cicloId, area_id: areaId, se, com_congeladas: 'true' });
    fetch(`${API}/fad01/conferencia-laboratorio?${qs}`)
      .then(r => (r.ok ? r.json() : null))
      .then(d => setConferencia(d?.grupos ?? null))
      .catch(() => setConferencia(null));
  }, [areaId, cicloId, se]);

  useEffect(() => { carregarConferencia(); }, [carregarConferencia]);

  // Filtros lembrados (Bloco F2): 'pendente' guarda o que falta restaurar;
  // so grava depois que a restauracao terminou, para nao apagar com valores vazios.
  const pendente = useRef<FiltrosFad07 | null>(lerFiltrosFad07());
  const [restaurado, setRestaurado] = useState(false);
  const [buscarAoRestaurar, setBuscarAoRestaurar] = useState(false);

  // ── Load reference data on mount ─────────────────────────────────────────
  useEffect(() => {
    fetch(`${API}/areas/`)
      .then(r => r.json())
      .then(setAreas)
      .catch(() => {});
    fetch(`${API}/ciclos/`)
      .then(r => r.json())
      .then(setCiclos)
      .catch(() => {});
  }, []);

  // Restaura area + ciclo quando as opcoes chegam (so se ainda existirem).
  useEffect(() => {
    if (restaurado || !areas.length || !ciclos.length) return;
    const p = pendente.current;
    if (p && p.areaId && p.cicloId
        && areas.some(a => String(a.id) === p.areaId) && ciclos.some(c => String(c.id) === p.cicloId)) {
      setAreaId(p.areaId);
      setCicloId(p.cicloId);   // a SE e restaurada quando as semanas chegarem
    } else {
      pendente.current = null;
      setRestaurado(true);
    }
  }, [areas, ciclos, restaurado]);

  useEffect(() => {
    if (restaurado) gravarFiltrosFad07({ areaId, cicloId, se });
  }, [restaurado, areaId, cicloId, se]);

  // ── Load semanas when area or ciclo changes ───────────────────────────────
  useEffect(() => {
    if (!areaId && !cicloId) {
      setSemanas([]);
      setSe('');
      return;
    }
    const qs = new URLSearchParams();
    if (areaId)  qs.set('area_id',  areaId);
    if (cicloId) qs.set('ciclo_id', cicloId);
    fetch(`${API}/fad07/semanas-disponiveis?${qs}`)
      .then(r => r.json())
      .then((data: SeInfo[]) => {
        setSemanas(data);
        const p = pendente.current;
        if (p && p.areaId === areaId && p.cicloId === cicloId) {
          pendente.current = null;
          if (p.se && data.some(s => String(s.se) === p.se)) {
            setSe(p.se);
            setBuscarAoRestaurar(true);
          } else {
            setSe('');
          }
          setRestaurado(true);
        } else {
          setSe('');
        }
      })
      .catch(() => {
        setSemanas([]);
        if (pendente.current) { pendente.current = null; setRestaurado(true); }
      });
  }, [areaId, cicloId]);

  // ── Fetch FAD07 data ──────────────────────────────────────────────────────
  const fetchDados = useCallback(() => {
    if (!areaId || !cicloId || !se) return;
    setLoading(true);
    setError('');
    const qs = new URLSearchParams({ se, area_id: areaId, ciclo_id: cicloId });
    fetch(`${API}/fad07/dados?${qs}`)
      .then(r => {
        if (!r.ok) throw new Error(`Erro ${r.status}: ${r.statusText}`);
        return r.json();
      })
      .then((data: DadosFAD07) => {
        setDadosFAD07(data);
        console.log('DADOS FAD07:', data);
        setDadosManuais(prev => ({
          ...DADOS_MANUAIS_DEFAULT,
          localidade:  prev.localidade,
          zona:        prev.zona,
          ciclo_ano:   prev.ciclo_ano,
          data_inicio: prev.data_inicio,
          data_final:  prev.data_final,
          sem_epidem:  prev.sem_epidem,
          tipo:        prev.tipo,
          concluido:   prev.concluido,
        }));
        setLoading(false);
      })
      .catch(e => {
        setError(e.message);
        setLoading(false);
      });
  }, [areaId, cicloId, se]);

  // Os 3 filtros restaurados: busca o documento uma vez, sozinho.
  useEffect(() => {
    if (!buscarAoRestaurar || !areaId || !cicloId || !se) return;
    setBuscarAoRestaurar(false);
    fetchDados();
  }, [buscarAoRestaurar, areaId, cicloId, se, fetchDados]);

  // Auto-preenche cabeçalho quando filtros mudam
  useEffect(() => {
    if (!areaId || !cicloId || !se || !semanas.length) return;
    const area   = areas.find(a => a.id === parseInt(areaId));
    const ciclo  = ciclos.find(c => c.id === parseInt(cicloId));
    const seNum  = parseInt(se);
    const seInfo = semanas.find(s => s.se === seNum);
    const ano    = seInfo
      ? new Date(seInfo.data_inicio + 'T12:00:00').getFullYear()
      : new Date().getFullYear();
    setDadosManuais(prev => ({
      ...prev,
      localidade:  area?.nome          || '',
      zona:        area?.zona          || '',
      ciclo_ano:   ciclo?.nome         || '',
      data_inicio: seInfo?.data_inicio || '',
      data_final:  seInfo?.data_fim    || '',
      sem_epidem:  se ? `${se}ª/${ano}` : '',
      tipo:        prev.tipo     || '1-sede',
      concluido:   prev.concluido,
    }));
  }, [areaId, cicloId, se, semanas, areas, ciclos]);

  const setManual = (field: keyof DadosManuais) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
      setDadosManuais(prev => ({ ...prev, [field]: e.target.value }));

  const setManualSelect = (field: keyof DadosManuais) =>
    (e: SelectChangeEvent) =>
      setDadosManuais(prev => ({ ...prev, [field]: e.target.value }));

  const todosPreenchidos = Boolean(areaId && cicloId && se);

  const handleImprimir = () => window.print();

  const handleBaixarPDF = () => {
    const elemento = document.getElementById('fad07-papel');
    if (!elemento) return;
    html2pdf().set({
      margin: [12, 10, 12, 10],
      filename: `FAD07_${dadosManuais.localidade || 'area'}_${dadosManuais.sem_epidem || 'se'}.pdf`,
      image: { type: 'jpeg', quality: 0.98 },
      html2canvas: { scale: 2, useCORS: true, backgroundColor: '#ffffff' },
      jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
    }).from(elemento).save();
  };

  const handleSalvar = async () => {
    try {
      const res = await fetch(`${API}/fad07/manual`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ area_id: areaId, ciclo_id: cicloId, se, ...dadosManuais }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.detail ?? `Erro ${res.status}: ${res.statusText}`);
      }
      alert('Dados salvos com sucesso');
    } catch (e: unknown) {
      alert(e instanceof Error ? e.message : 'Erro ao salvar');
    }
  };

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: '#F0F4FF', display: 'flex', flexDirection: 'column' }}>
      <style>{`
        @page { size: A4 portrait; margin: 12mm 10mm; }
        @media print {
          body { background: #ffffff !important; }
          .MuiAppBar-root, .MuiDrawer-root { display: none !important; }
          .noprint { display: none !important; }
        }
      `}</style>
      <ModuleHeader
        titulo="FAD-07 Semanal"
        subtitulo="Consolidado semanal por área"
        icone={<SummarizeIcon sx={{ fontSize: 32 }} />}
        accentColor="#0277BD"
        onVoltar={onVoltar}
      />
      <Box sx={{ p: 3, flexGrow: 1 }}>

      {/* ── Faixa de filtros ── */}
      <Box className="noprint" sx={{
        display: 'flex', flexWrap: 'wrap', gap: 2,
        alignItems: 'center', mb: 3,
        p: 2, background: '#fff', borderRadius: 2,
        boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
      }}>
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>Área</InputLabel>
          <Select
            value={areaId}
            label="Área"
            onChange={(e: SelectChangeEvent) => setAreaId(e.target.value)}
          >
            <MenuItem value=""><em>— Selecione —</em></MenuItem>
            {areas.map(a => (
              <MenuItem key={a.id} value={String(a.id)}>
                {a.nome} ({a.zona})
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>Ciclo</InputLabel>
          <Select
            value={cicloId}
            label="Ciclo"
            onChange={(e: SelectChangeEvent) => setCicloId(e.target.value)}
          >
            <MenuItem value=""><em>— Selecione —</em></MenuItem>
            {ciclos.map(c => (
              <MenuItem key={c.id} value={String(c.id)}>
                {c.nome}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 240 }} disabled={semanas.length === 0}>
          <InputLabel>Semana Epidemiológica</InputLabel>
          <Select
            value={se}
            label="Semana Epidemiológica"
            onChange={(e: SelectChangeEvent) => setSe(e.target.value)}
          >
            <MenuItem value=""><em>— Selecione —</em></MenuItem>
            {semanas.map(s => (
              <MenuItem key={s.se} value={String(s.se)}>
                {semanaLabel(s)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Button
          variant="contained"
          startIcon={<RefreshIcon />}
          onClick={() => { fetchDados(); carregarConferencia(); }}
          disabled={!todosPreenchidos || loading}
          sx={{ height: 40 }}
        >
          Atualizar dados
        </Button>
      </Box>

      <Box className="noprint" sx={{ display: 'flex', gap: 2, mt: 2 }}>
        <Button variant="contained" color="primary" onClick={handleSalvar}>
          Salvar dados manuais
        </Button>
        <Button variant="outlined" onClick={handleImprimir}>
          Imprimir
        </Button>
        <Button variant="outlined" onClick={handleBaixarPDF}>
          Baixar PDF
        </Button>
        <BotaoArquivar alvo={() => document.getElementById('fad07-papel')} pedido={pedidoArquivo} disabled={loading} />
      </Box>

      {/* ── Conteúdo condicional ── */}
      {!todosPreenchidos && !dadosFAD07 && (
        <Alert severity="info" sx={{ maxWidth: 520 }}>
          Selecione Área, Ciclo e Semana Epidemiológica para gerar o FAD-07.
        </Alert>
      )}

      {loading && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 2 }}>
          <CircularProgress size={24} />
          <Typography variant="body2" color="text.secondary">Carregando dados...</Typography>
        </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ mt: 2, maxWidth: 520 }}>
          {error}
        </Alert>
      )}

      {/* Alerta do laboratório da semana — fora do papel (#fad07-papel) e da impressão (.noprint) */}
      {todosPreenchidos && !!conferencia?.length && (
        <Box className="noprint" sx={{ mt: 3 }}>
          <AlertaLaboratorioSemana grupos={conferencia} modo="semana" className="noprint" onAbrirFAD01={onAbrirFAD01} />
        </Box>
      )}

      {dadosFAD07 && !loading && (
        <Box id="fad07-papel" sx={{
          mt: 3,
          maxWidth: 794,
          mx: 'auto',
          p: '48px',
          background: '#fff',
          border: '1px solid #E0E0E0',
          boxShadow: '0 2px 8px rgba(0,0,0,0.10)',
          borderRadius: 1,
        }}>
          <Typography variant="h6" align="center" sx={{ fontWeight: 700, letterSpacing: 0.5, mb: 0.5 }}>
            RESUMO SEMANAL DO SERVIÇO ANTIVETORIAL
          </Typography>
          <Typography variant="subtitle2" align="center" sx={{ color: 'text.secondary', letterSpacing: 0.3, mb: 3 }}>
            FAD-07 — Semanal · PNCD
          </Typography>

          {/* ── (02–05) Identificação ── */}
          <Grid container spacing={2}>
            <Grid size={3}>
              <TextField
                size="small" fullWidth label="(02) Município"
                value="Milagres"
                slotProps={{ htmlInput: { readOnly: true } }}
              />
            </Grid>
            <Grid size={3}>
              <TextField
                select size="small" fullWidth label="(03) Localidade"
                value={dadosManuais.localidade}
                onChange={setManual('localidade')}
                slotProps={{ inputLabel: { shrink: true } }}
              >
                {areas.map(a => (
                  <MenuItem key={a.id} value={a.nome}>{a.nome}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid size={3}>
              <TextField
                select size="small" fullWidth label="(04) Zona"
                value={dadosManuais.zona}
                onChange={setManual('zona')}
                slotProps={{ inputLabel: { shrink: true } }}
              >
                <MenuItem value="Urbana">Urbana</MenuItem>
                <MenuItem value="Rural">Rural</MenuItem>
              </TextField>
            </Grid>
            <Grid size={3}>
              <TextField
                size="small" fullWidth label="(05) Categ"
                value="BRR"
                slotProps={{ htmlInput: { readOnly: true } }}
              />
            </Grid>
          </Grid>

          {/* ── (06–11) Período / controle ── */}
          <Grid container spacing={2} sx={{ mt: 3 }}>
            <Grid size={{ xs: 6, sm: 4, md: 4 }}>
              <TextField
                select size="small" fullWidth
                label="(06) Tipo"
                value={dadosManuais.tipo}
                onChange={setManual('tipo')}
                slotProps={{ inputLabel: { shrink: true } }}
                sx={{ '& .MuiInputLabel-root': { backgroundColor: 'white', px: 0.5 } }}
              >
                <MenuItem value=""><em>—</em></MenuItem>
                <MenuItem value="1-sede">1-sede</MenuItem>
                <MenuItem value="2-outros">2-outros</MenuItem>
              </TextField>
            </Grid>
            <Grid size={{ xs: 6, sm: 4, md: 4 }}>
              <TextField
                select size="small" fullWidth label="(07) Ciclo/Ano"
                value={dadosManuais.ciclo_ano}
                onChange={setManual('ciclo_ano')}
                slotProps={{ inputLabel: { shrink: true } }}
                sx={{ '& .MuiInputLabel-root': { backgroundColor: 'white', px: 0.5 } }}
              >
                {ciclos.map(c => (
                  <MenuItem key={c.id} value={c.nome}>{c.nome}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid size={{ xs: 6, sm: 4, md: 4 }}>
              <TextField
                size="small" fullWidth label="(08) Data início"
                type="date"
                value={dadosManuais.data_inicio}
                onChange={setManual('data_inicio')}
                slotProps={{ inputLabel: { shrink: true } }}
                sx={{ '& .MuiInputLabel-root': { backgroundColor: 'white', px: 0.5 } }}
              />
            </Grid>
            <Grid size={{ xs: 6, sm: 4, md: 4 }}>
              <TextField
                size="small" fullWidth label="(09) Data final"
                type="date"
                value={dadosManuais.data_final}
                onChange={setManual('data_final')}
                slotProps={{ inputLabel: { shrink: true } }}
                sx={{ '& .MuiInputLabel-root': { backgroundColor: 'white', px: 0.5 } }}
              />
            </Grid>
            <Grid size={{ xs: 6, sm: 4, md: 4 }}>
              <TextField
                select size="small" fullWidth
                label="(10) Concluído"
                value={dadosManuais.concluido}
                onChange={setManual('concluido')}
                slotProps={{ inputLabel: { shrink: true } }}
                sx={{ '& .MuiInputLabel-root': { backgroundColor: 'white', px: 0.5 } }}
              >
                <MenuItem value=""><em>—</em></MenuItem>
                <MenuItem value="S">S</MenuItem>
                <MenuItem value="N">N</MenuItem>
              </TextField>
            </Grid>
            <Grid size={{ xs: 6, sm: 4, md: 4 }}>
              <TextField
                select size="small" fullWidth label="(11) Sem.Epidem."
                value={dadosManuais.sem_epidem}
                onChange={setManual('sem_epidem')}
                slotProps={{ inputLabel: { shrink: true } }}
                sx={{ '& .MuiInputLabel-root': { backgroundColor: 'white', px: 0.5 } }}
              >
                {semanas.map(s => (
                  <MenuItem key={s.se} value={`${s.se}ª/${new Date().getFullYear()}`}>
                    {`${s.se}ª/${new Date().getFullYear()}`}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
          </Grid>

          {/* ── (12) Atividade ── */}
          <Box sx={{ mt: 2 }}>
            <FormControl size="small" fullWidth>
              <InputLabel id="atividade-label">{atividadeLabel}</InputLabel>
              <Select
                labelId="atividade-label"
                value={dadosManuais.atividade}
                label={atividadeLabel}
                onChange={setManualSelect('atividade')}
              >
                {ATIVIDADES.map(a => (
                  <MenuItem key={a.value} value={a.value}>{a.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          {/* ── RESUMO DO TRABALHO DE CAMPO ── */}
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mt: 4, mb: 1 }}>
            RESUMO DO TRABALHO DE CAMPO
          </Typography>
          <Divider sx={{ mb: 2 }} />

          {/* ── (14–19) Imóveis por tipo ── */}
          <Grid container spacing={2}>
            <Grid size={2}>
              <TextField
                size="small" fullWidth
                label={spLabel(14, 'Residência')}
                value={dadosFAD07.computado.residencias ?? 0}
                slotProps={{ htmlInput: roTabNums }}
              />
            </Grid>
            <Grid size={2}>
              <TextField
                size="small" fullWidth
                label={spLabel(15, 'Comércio')}
                value={dadosFAD07.computado.comercios ?? 0}
                slotProps={{ htmlInput: roTabNums }}
              />
            </Grid>
            <Grid size={2}>
              <TextField
                size="small" fullWidth
                label={spLabel(16, 'TB')}
                value={dadosFAD07.computado.terrenos_baldios ?? 0}
                slotProps={{ htmlInput: roTabNums }}
              />
            </Grid>
            <Grid size={2}>
              <TextField
                size="small" fullWidth
                label={spLabel(17, 'PE')}
                value={dadosFAD07.computado.pontos_estrategicos ?? 0}
                slotProps={{ htmlInput: roTabNums }}
              />
            </Grid>
            <Grid size={2}>
              <TextField
                size="small" fullWidth
                label={spLabel(18, 'Outro')}
                value={dadosFAD07.computado.outros ?? 0}
                slotProps={{ htmlInput: roTabNums }}
              />
            </Grid>
            <Grid size={2}>
              <TextField
                size="small" fullWidth
                label={spLabel(19, 'Total')}
                value={dadosFAD07.computado.total_imoveis ?? 0}
                slotProps={{ htmlInput: roTabNums }}
              />
            </Grid>
          </Grid>

          {/* ── (13, 20–22) Quarteirões / Visitas ── */}
          <Grid container spacing={2} sx={{ mt: 2 }}>
            <Grid size={3}>
              <TextField
                size="small" fullWidth
                label={spLabel(13, 'Total quart. concl.')}
                value={dadosFAD07.computado.quarteiroes_concluidos_count ?? 0}
                slotProps={{ htmlInput: roTabNums }}
              />
            </Grid>
            <Grid size={3}>
              <TextField
                size="small" fullWidth
                label={spLabel(20, 'Trat.Focal')}
                value={dadosFAD07.computado.trat_focal ?? 0}
                slotProps={{ htmlInput: roTabNums }}
              />
            </Grid>
            <Grid size={3}>
              <TextField
                size="small" fullWidth
                label={spLabel(21, 'Trat.Perifocal')}
                value={dadosFAD07.computado.trat_perifocal ?? 0}
                slotProps={{ htmlInput: roTabNums }}
              />
            </Grid>
            <Grid size={3}>
              <TextField
                size="small" fullWidth
                label={spLabel(22, 'Inspecionados')}
                value={dadosFAD07.computado.inspecionados ?? 0}
                slotProps={{ htmlInput: roTabNums }}
              />
            </Grid>
          </Grid>

          {/* ── (23–26) Amostras / Situação ── */}
          <Grid container spacing={2} sx={{ mt: 2 }}>
            <Grid size={3}>
              <TextField
                size="small" fullWidth
                label={spLabel(23, 'Amostras Coletadas')}
                value={dadosFAD07.computado.amostras_coletadas ?? 0}
                slotProps={{ htmlInput: roTabNums }}
              />
            </Grid>
            <Grid size={3}>
              <TextField
                size="small" fullWidth
                label={spLabel(24, 'Recusa')}
                value={dadosFAD07.computado.recusados ?? 0}
                slotProps={{ htmlInput: roTabNums }}
              />
            </Grid>
            <Grid size={3}>
              <TextField
                size="small" fullWidth
                label={spLabel(25, 'Fechados')}
                value={dadosFAD07.computado.fechados ?? 0}
                slotProps={{ htmlInput: roTabNums }}
              />
            </Grid>
            <Grid size={3}>
              <TextField
                size="small" fullWidth
                label={spLabel(26, 'Recuperados')}
                value={dadosFAD07.computado.recuperados ?? 0}
                slotProps={{ htmlInput: roTabNums }}
              />
            </Grid>
          </Grid>

          {/* ── (27–34) Depósitos por tipo ── */}
          <Typography variant="caption" sx={{
            display: 'block', mt: 3, fontWeight: 600,
            color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.5,
          }}>
            Nº depósitos inspecionados por tipo
          </Typography>
          <Grid container spacing={1}>
            <Grid size={1.5}>
              <TextField
                size="small" fullWidth
                label={spLabel(27, 'A1')}
                value={dadosFAD07.computado.dep_a1 ?? 0}
                slotProps={{ htmlInput: roTabNums }}
              />
            </Grid>
            <Grid size={1.5}>
              <TextField
                size="small" fullWidth
                label={spLabel(28, 'A2')}
                value={dadosFAD07.computado.dep_a2 ?? 0}
                slotProps={{ htmlInput: roTabNums }}
              />
            </Grid>
            <Grid size={1.5}>
              <TextField
                size="small" fullWidth
                label={spLabel(29, 'B')}
                value={dadosFAD07.computado.dep_b ?? 0}
                slotProps={{ htmlInput: roTabNums }}
              />
            </Grid>
            <Grid size={1.5}>
              <TextField
                size="small" fullWidth
                label={spLabel(30, 'C')}
                value={dadosFAD07.computado.dep_c ?? 0}
                slotProps={{ htmlInput: roTabNums }}
              />
            </Grid>
            <Grid size={1.5}>
              <TextField
                size="small" fullWidth
                label={spLabel(31, 'D1')}
                value={dadosFAD07.computado.dep_d1 ?? 0}
                slotProps={{ htmlInput: roTabNums }}
              />
            </Grid>
            <Grid size={1.5}>
              <TextField
                size="small" fullWidth
                label={spLabel(32, 'D2')}
                value={dadosFAD07.computado.dep_d2 ?? 0}
                slotProps={{ htmlInput: roTabNums }}
              />
            </Grid>
            <Grid size={1.5}>
              <TextField
                size="small" fullWidth
                label={spLabel(33, 'E')}
                value={dadosFAD07.computado.dep_e ?? 0}
                slotProps={{ htmlInput: roTabNums }}
              />
            </Grid>
            <Grid size={1.5}>
              <TextField
                size="small" fullWidth
                label={spLabel(34, 'Total')}
                value={dadosFAD07.computado.dep_total ?? 0}
                slotProps={{ htmlInput: roTabNums }}
              />
            </Grid>
          </Grid>

          {/* ── (35–38) Depósito / Larvicida ── */}
          <Grid container spacing={2} sx={{ mt: 3 }}>
            <Grid size={3}>
              <TextField
                size="small" fullWidth
                label={spLabel(35, 'Depósito Eliminado')}
                value={dadosFAD07.computado.dep_eliminados ?? 0}
                slotProps={{ htmlInput: roTabNums }}
              />
            </Grid>
            <Grid size={3}>
              <TextField
                size="small" fullWidth
                label={spLabel(36, 'Tipo Larvicida')}
                value={dadosFAD07.computado.larvicida_l1_tipo ?? ''}
                slotProps={{ htmlInput: { readOnly: true } }}
              />
            </Grid>
            <Grid size={3}>
              <TextField
                size="small" fullWidth
                label={spLabel(37, 'Qtde Gramas')}
                value={Number(dadosFAD07.computado.larvicida_l1_gramas ?? 0).toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}
                slotProps={{ htmlInput: roTabNums }}
              />
            </Grid>
            <Grid size={3}>
              <TextField
                size="small" fullWidth
                label={spLabel(38, 'Qtde Dep.Trat.')}
                value={dadosFAD07.computado.larvicida_l1_depositos ?? 0}
                slotProps={{ htmlInput: roTabNums }}
              />
            </Grid>
          </Grid>

          {/* ── (39–44) Adulticida / Agentes ── */}
          <Grid container spacing={2} sx={{ mt: 3 }}>
            <Grid size={3}>
              <TextField
                size="small" fullWidth
                label={spLabel(39, 'Adulticida Tipo')}
                value={dadosFAD07.computado.adulticida_tipo ?? ''}
                slotProps={{ htmlInput: { readOnly: true } }}
              />
            </Grid>
            <Grid size={3}>
              <TextField
                size="small" fullWidth
                label={spLabel(40, 'Adulticida Qtde cargas')}
                value={dadosFAD07.computado.adulticida_cargas ?? 0}
                slotProps={{ htmlInput: roTabNums }}
              />
            </Grid>
            <Grid size={3}>
              <TextField
                size="small" fullWidth
                label={spLabel(43, 'Total de Agentes na Semana')}
                value={dadosFAD07.computado.agentes_trabalhando ?? 0}
                slotProps={{ htmlInput: roTabNums }}
              />
            </Grid>
            <Grid size={3}>
              <TextField
                size="small" fullWidth
                label={spLabel(44, 'Total de Dias trabalhados')}
                value={dadosFAD07.computado.dias_trabalhados ?? 0}
                slotProps={{ htmlInput: roTabNums }}
              />
            </Grid>
          </Grid>

          {/* ── Nota de instrução ── */}
          <Box sx={{ mt: 2 }}>
            <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: 11, display: 'block', lineHeight: 1.5 }}>
              <strong>Atenção!</strong><br />
              37 - Inserir a quantidade de larvicida em gramas (ex. 2 cargas = 1.000g)<br />
              43 - Total de Agentes na semana (Ex. Seg=1, Ter=1, Qua=1, Qui=1, Sex=1 Total=5)<br />
              44 - Total de Dias trabalhados na semana (Ex. Seg=1, Ter=1, Qua=1, Qui=1, Sex=1 Total=5)
            </Typography>
          </Box>

          {/* ── Accordion Laboratório ── */}
          <Accordion
            expanded={labExpandido}
            defaultExpanded={true}
            onChange={() => setLabExpandido(prev => !prev)}
            sx={{ mt: 4 }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                Resumo do Laboratório (preenchido pelo sistema)
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
                  <FAD07Laboratorio dados={dadosFAD07} />
                </AccordionDetails>
          </Accordion>
          {/* ── (66–67) Visto do supervisor ── */}
          <Grid container spacing={2} sx={{ mt: 4 }}>
            <Grid size={6}>
              <TextField
                size="small" fullWidth
                label={spLabel(66, 'Visto do Supervisor')}
                value={dadosManuais.visto_supervisor}
                onChange={setManual('visto_supervisor')}
              />
            </Grid>
            <Grid size={6}>
              <TextField
                size="small" fullWidth
                label={spLabel(67, 'Data do visto')}
                type="date"
                value={dadosManuais.data_visto}
                onChange={setManual('data_visto')}
                slotProps={{ inputLabel: { shrink: true } }}
              />
            </Grid>
          </Grid>

          <Typography variant="caption" sx={{ mt: 3, display: 'block', textAlign: 'center', fontSize: 10, color: 'text.disabled' }}>
            Documento gerado pelo sistema Tovis - criado por Walber Reis
          </Typography>
        </Box>
      )}
      </Box>
    </Box>
  );
}
